<template lang="pug">
div
    div(class="d-flex flex-column main-container p-2" v-if="!loading && !error && fowEligible === false")
        div(class="feature-banner")
            b-icon(icon="info-circle" class="mr-2")
            div
                strong FOW clients only
                p.mb-0 Session swaps are available for clients attending Family of Wisdom (FOW) sessions. This client currently has no active FOW schedule.
        b-button(variant="outline-primary" size="sm" class="align-self-start" @click="$router.replace('/home')") Back to Home

    div(class="d-flex flex-column main-container p-2" v-else-if="!loading && !error")
        //- For FOW clients only; replacements must fall after the coming weekend
        div(class="feature-banner feature-banner--swap" data-tour="intro")
            b-icon(icon="arrow-repeat" class="mr-2")
            div(class="flex-grow-1")
                strong Make Up Session
                p.mb-0 Swap an upcoming FOW session for another. Replacement dates must be #[strong after this coming weekend] (next week onward) — a session can't be moved within the current week. Sessions in a future week can be swapped to another day of that same week.

            b-button(size="sm" variant="outline-dark" class="howto-btn ml-2 flex-shrink-0" @click="$refs.tour.start()" title="Show me how")
                b-icon(icon="question-circle" class="mr-1")
                | How to

        how-to-tour(ref="tour" :steps="tourSteps" storage-key="howto_swap_v1" auto-start)

        b-tabs.card-pills.card-accent-tabs(class="w-100" ref="mainTabs" v-model="activeTab")
            b-tab(title="Swap Session" :title-link-class="'text-primary'")
                SwapSessionTab(:userId="userId" @request-submitted="handleRequestSubmitted" ref="swapTab")

            b-tab(title="My Swaps" :title-link-class="'text-info'" :title-link-attributes="{ 'data-tour': 'my-requests' }")
                MyRequestsTab(:userId="userId" type-filter="swap" ref="myRequestsTab")

    div(v-if="loading" class="d-flex w-100 h-100 flex-column align-items-center p-2")
        div(class="tabs-skeleton w-100 d-flex" style="height: 50px;gap:5px;" )
            b-skeleton(height="5vh" width="30%" class="my-2" v-for="i in 2" :key="i")
        b-skeleton(height="30vh" width="100%" class="my-2")
        b-skeleton(height="30vh" width="100%" class="my-2")

    div(v-if="error" class="d-flex w-100 h-100 flex-column align-items-center justify-content-center p-2")
        lottie-animation(:animation-data="animationData" :autoPlay="true" :loop="true" :speed="1" ref="anim"  class="w-50")
        p(class="text-danger text-center") Its not you, its us. Something went wrong. Please try again later.
        b-button(@click="initialize" variant="primary" class="mt-2") Try Again
</template>

<script>
import errorAnimation from "@/assets/errorAnimation.json";
import LottieAnimation from 'lottie-web-vue';
import SwapSessionTab from '@/components/makeup/SwapSessionTab.vue';
import MyRequestsTab from '@/components/makeup/MyRequestsTab.vue';
import HowToTour from '@/components/makeup/HowToTour.vue';
import fowEligibility from '@/mixins/fowEligibility';

export default {
    mixins: [fowEligibility],
    components: {
        LottieAnimation,
        SwapSessionTab,
        MyRequestsTab,
        HowToTour
    },
    data() {
        return {
            animationData: errorAnimation,
            loading: true,
            error: false,
            userId: null,
            activeTab: 0,
            tourSteps: [
                { title: 'Make Up Session', icon: 'arrow-repeat', target: 'intro', text: 'Use this page to swap an upcoming FOW session for another day. Tap <b>How to</b> any time to see these steps again.' },
                { title: '1 · Tap the session you will miss', icon: 'calendar-check', target: 'swap-pick', text: 'Your upcoming sessions are listed by day. Tap the one you cannot attend — greyed ones are already swapped or covered by an MC.' },
                { title: '2 · Pick a replacement date', icon: 'calendar-plus', target: 'swap-replacement', text: 'Only dates <b>after this coming weekend</b> (and after the session you tapped) are offered, grouped by day with free seats shown. Two sessions can run at the same time — the small <b>CN / EN</b> tag tells you the language group. Tap the date you want.' },
                { title: '3 · Confirm', icon: 'check2-circle', text: 'A confirmation pops up showing the session you will <b>miss</b> and the one you will <b>attend instead</b>. Tap <b>Confirm swap</b> to submit, or <b>Back</b> to pick another date. Swaps are applied automatically — no approval needed.' },
                { title: '4 · Track it', icon: 'list-check', target: 'my-requests', text: 'Your swaps appear under <b>My Swaps</b>. You can hold up to 3 upcoming swaps at a time.' },
            ],
        };
    },
    async mounted() {
        await this.initialize();
    },
    methods: {
        async initialize() {
            this.loading = true;
            this.error = false;

            try {
                await this.loadCurrentUser();
                this.userId = this.$root.currentUser[0].crb5c_client.crb5c_fow_customerid;
                if (!this.userId) {
                    throw new Error('User ID not found');
                }
                await this.ensureFowEligibility();
                this.loading = false;
            } catch (err) {
                console.error('Error initializing swap session page:', err);
                this.error = true;
                this.loading = false;
            }
        },
        async loadCurrentUser() {
            while (!this.$root.currentUser[0]) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }
        },
        handleRequestSubmitted() {
            // Jump to the "My ..." tab so the new request is visible right away
            // (the success modal renders over it).
            this.activeTab = 1;
            if (this.$refs.myRequestsTab) {
                this.$refs.myRequestsTab.loadRequests();
            }
            if (this.$refs.swapTab) {
                this.$refs.swapTab.loadScheduledDates();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.main-container {
    min-height: 100vh;
    background-color: #f5f5f5;
}

.howto-btn {
    font-size: 0.75rem;
    font-weight: 700;
    white-space: nowrap;
    border-radius: 999px;
    padding: 0.2rem 0.6rem;
    align-self: flex-start;
}

.feature-banner {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    background: #eef4ff;
    border-left: 4px solid #3b82f6;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    margin-bottom: 0.75rem;
    font-size: 0.85rem;
    text-align: left;
}
</style>
